import isEmpty from 'lodash/isEmpty';
import differenceWith from 'lodash/differenceWith';
import groupBy from 'lodash/groupBy';

import {ChartType, ResourceType} from 'common/report/constants';
import {DateRangeAttributes} from 'common/widgets/date-range-picker/models/date-range';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Segmentation} from 'common/types/reports/query';
import {CohortSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {CohortFilterTree} from 'common/query-builder/query-block/filter-menu/types';
import {
  bookmarkFilterByCohortToCohortFilters,
  getFilterByCohorts,
} from 'common/query-builder/query-block/filter-menu/util';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {LogicalOperator} from 'common/widgets/property-filter-menu/models/types';
import type {BookmarkFilterByEvent} from 'common/report/funnels/models/types';
import {InsightsReportSectionsOverride} from 'common/report/insights/models/types';

export type DashboardBookmarkOverrides = {
  additionalFilters?: Segmentation[];
  chartTypeOverride?: ChartType;
  dateRangeOverride?: DateRangeAttributes;
};

export type BookmarkOverrides = DashboardBookmarkOverrides & {
  originDashboard?: DashboardOrigin;
  // Only used in funnels and insights
  erfTreatments?: CohortSegment[];
  // Only used in funnels
  groupByOverride?: Segment[];
  timeUnitOverride?: TimeUnit;
  trendUnitOverride?: TimeUnit;
};

export const BookmarkOverrides: Array<keyof BookmarkOverrides> = [
  `additionalFilters`,
  `chartTypeOverride`,
  `originDashboard`,
  `dateRangeOverride`,
  `erfTreatments`,
  `groupByOverride`,
  `timeUnitOverride`,
  `trendUnitOverride`,
];

export type DashboardOrigin = {
  id: number;
  title: string;
  creator_name?: string;
  grid_details?: any;

  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;
  // TODO @gonzalo or @shayan: Temporary param to know when we should use the new grid mode. Remove when grid is GA
  layout_version?: string;
};

export function getDashboardParams(dashboard, {urlEncode = false} = {}): DashboardOrigin | EmptyObj {
  if (isEmpty(dashboard)) {
    return {};
  }
  return {
    id: dashboard.id,
    title: urlEncode ? encodeURIComponent(dashboard.title) : dashboard.title,
    ...(dashboard.grid_details ? {grid_details: dashboard.grid_details} : {}),
  };
}

/**
 * This logic is reused for funnels, flows, and retention bookmarks - hence the loose types in args
 */
export type BookmarkFilters = {
  filter_by_event?: BookmarkFilterByEvent;
  filter_by_cohort?: CohortFilterTree | EmptyObj;
};
export function getMergedBookmarkEventAndCohortFilters(
  bookmarkFilters: BookmarkFilters,
  additionalFilters: PropertyFilter[],
): BookmarkFilters {
  const {filter_by_event: filterByEvent, filter_by_cohort: filterByCohort} = bookmarkFilters;

  // Step 1: deduplicate any additional filters provided
  const eventAndUserPropertyFiltersFromBookmark =
    filterByEvent?.children?.map((bookmarkFilter) => new PropertyFilter(Object.values(bookmarkFilter)[0])) ?? [];
  const cohortFiltersFromBookmark = bookmarkFilterByCohortToCohortFilters(filterByCohort ?? {});

  const filtersToAppend = differenceWith(
    additionalFilters,
    [...eventAndUserPropertyFiltersFromBookmark, ...cohortFiltersFromBookmark],
    (f1, f2) => f1.equals(f2),
  );

  // Step 2: append any new event/user filters to filter_by_event and cohort filters to filter_by_cohort
  const groupedFilters = groupBy(filtersToAppend, (filter) => filter.resourceType);
  const mergedFilterByEvent = {
    operator: LogicalOperator.And,
    children: [
      ...(filterByEvent?.children ?? []),
      ...(groupedFilters[ResourceType.Event] ?? []).map((filter) => ({event: filter.getAttributes()})),
      ...(groupedFilters[ResourceType.User] ?? []).map((filter) => ({user: filter.getAttributes()})),
    ],
  };

  const cohortPropertyFilters = [...(groupedFilters[ResourceType.Cohort] ?? []), ...cohortFiltersFromBookmark];
  const mergedFilterByCohort = cohortPropertyFilters.length ? getFilterByCohorts(cohortPropertyFilters) : null;

  return {
    ...(mergedFilterByEvent.children.length ? {filter_by_event: mergedFilterByEvent} : {}),
    ...(mergedFilterByCohort ? {filter_by_cohort: mergedFilterByCohort} : {}),
  };
}

export type InsightsUrlOverrides = BookmarkOverrides & {
  description?: string;
  querySamplingEnabled?: boolean;
  // For overriding report sections. Used by ERF.
  reportSectionsOverride?: InsightsReportSectionsOverride;
  title?: string;
};

export const InsightsUrlOverrideKeys: Array<keyof InsightsUrlOverrides> = [
  ...BookmarkOverrides,
  `description`,
  `querySamplingEnabled`,
  `reportSectionsOverride`,
  `title`,
];
